import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  Key, Lock, ShieldCheck, CheckCircle2, XCircle, Bell, Mail, Smartphone, Cake,
  AlertTriangle, BarChart3, Download, Palette, Sun, Moon, Monitor, Globe,
  Library, BookOpen, MessageSquare, Video, HelpCircle, RefreshCw, LogOut,
  Hand, User, Settings, Rocket, Search, Loader2
} from 'lucide-react';
import { 
  logoutUser, 
  logoutAllDevices,
  changePassword,
  toggleTwoFactor,
  selectCurrentUser, 
  selectIsAuthenticated,
  selectUserName,
  selectUserEmail,
  selectUserRole,
  selectCurrentOrganization,
  selectApiMessage,
  selectAuthError,
  clearApiMessage,
  clearError,
  clearAuthState
} from '../../store/slices/authSlice';
import './Layout.css';

const Topbar = ({ title, onThemeChange, onLanguageChange, showToast }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isThemeSubmenuOpen, setIsThemeSubmenuOpen] = useState(false);
  const [isLanguageSubmenuOpen, setIsLanguageSubmenuOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('light');
  const [currentLanguage, setCurrentLanguage] = useState('English');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef(null);
  const avatarRef = useRef(null);

  // Password change refs
  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  // Get user from Redux store with safe defaults
  const currentUser = useSelector(selectCurrentUser) || {};
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userName = useSelector(selectUserName) || '';
  const userEmail = useSelector(selectUserEmail) || '';
  const userRole = useSelector(selectUserRole) || '';
  
  // IMPORTANT: Safely extract organization as a string
  const organization = useSelector(selectCurrentOrganization);
  // Ensure organization is a string, not an object
  const organizationName = typeof organization === 'string' 
    ? organization 
    : (organization?.name || organization?.title || organization?.organization_name || '');

  const apiMessage = useSelector(selectApiMessage);
  const authError = useSelector(selectAuthError);

  // Display API messages
  useEffect(() => {
    if (apiMessage && showToast && shouldShowApiToast(apiMessage, !!authError)) {
      showToast(apiMessage, authError ? 'error' : 'success');
    }
    if (apiMessage && showToast) {
      setTimeout(() => {
        dispatch(clearApiMessage());
        dispatch(clearError());
      }, 5000);
    }
  }, [apiMessage, authError, dispatch, showToast]);

  // Get user initials safely
  const getUserInitials = (userData) => {
    if (!userData) return '??';
    const name = userData.name || userName || '';
    if (!name) return '??';
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0][0] || '?') + (nameParts[1][0] || '?');
    }
    return nameParts[0][0] || '??';
  };

  // Get user status color safely
  const getStatusColor = (status) => {
    if (!status) return '#f59e0b';
    const statusLower = String(status).toLowerCase();
    switch(statusLower) {
      case 'active': return '#22c55e';
      case 'inactive': return '#ef4444';
      default: return '#f59e0b';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          avatarRef.current && !avatarRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsThemeSubmenuOpen(false);
        setIsLanguageSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setIsThemeSubmenuOpen(false);
    setIsLanguageSubmenuOpen(false);
  };

  // ==================== TOAST HELPER ====================

  const showToastMessage = (message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  };

  // ==================== PROFILE ACTIONS ====================

  const handleViewProfile = () => {
    setIsDropdownOpen(false);
    navigate('/profile');
  };

  // ==================== SETTINGS ACTIONS ====================

  const handleSettings = () => {
    setIsDropdownOpen(false);
    navigate('/settings');
  };

  // ==================== PASSWORD ACTIONS ====================

  const handleChangePassword = () => {
    setIsDropdownOpen(false);
    dispatch(clearApiMessage());
    dispatch(clearError());

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Key size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Change Password</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input 
              className="form-input" 
              type="password" 
              ref={currentPasswordRef}
              placeholder="Enter your current password" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input 
              className="form-input" 
              type="password" 
              ref={newPasswordRef}
              placeholder="Enter new password" 
            />
            <div className="form-hint">Minimum 8 characters with at least one number and special character</div>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input 
              className="form-input" 
              type="password" 
              ref={confirmPasswordRef}
              placeholder="Confirm new password" 
            />
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--blue-bg)', 
            borderRadius: '8px', 
            fontSize: '12px', 
            color: 'var(--blue)' 
          }}>
            <Lock size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Your password will be securely encrypted.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const current = currentPasswordRef.current?.value || '';
              const newPass = newPasswordRef.current?.value || '';
              const confirm = confirmPasswordRef.current?.value || '';

              if (!current) {
                showToastMessage('Please enter your current password', 'error');
                return;
              }
              if (newPass.length < 8) {
                showToastMessage('Password must be at least 8 characters', 'error');
                return;
              }
              if (newPass !== confirm) {
                showToastMessage('Passwords do not match', 'error');
                return;
              }

              setIsSubmitting(true);

              const result = await dispatch(changePassword({
                current_password: current,
                new_password: newPass,
                new_password_confirmation: confirm
              }));

              setIsSubmitting(false);

              if (result.payload?.success) {
                closeModal();
              }
            }}
          >
            {isSubmitting
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Changing...</>
              : <><Key size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Change Password</>}
          </button>
        </div>
      </div>
    );
  };

  // ==================== TWO-FACTOR AUTHENTICATION ====================

  const handleTwoFactorAuth = () => {
    setIsDropdownOpen(false);
    dispatch(clearApiMessage());
    dispatch(clearError());

    // Safely get two_factor_enabled with fallback
    const isTwoFactorEnabled = currentUser?.two_factor_enabled || false;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><ShieldCheck size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Two-Factor Authentication</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            textAlign: 'center',
            padding: '20px 0'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><Lock size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--navy)' }}>
              Two-Factor Authentication
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginTop: '8px' }}>
              Add an extra layer of security to your account.
            </p>
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--text-mid)',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>Status:</span>
                <span style={{ color: isTwoFactorEnabled ? 'var(--green)' : 'var(--amber)', fontWeight: '600' }}>
                  {isTwoFactorEnabled
                    ? <><CheckCircle2 size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Enabled</>
                    : <><XCircle size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Disabled</>}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>Method:</span>
                <span>SMS Verification</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>Phone:</span>
                <span>{currentUser?.phone || 'Not set'}</span>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number for Verification</label>
            <input 
              className="form-input" 
              defaultValue={currentUser?.phone || ''}
              placeholder="+233244123456"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button 
            className="btn btn-danger btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              const result = await dispatch(toggleTwoFactor());
              setIsSubmitting(false);
              if (result.payload?.success) {
                closeModal();
              }
            }}
          >
            {isSubmitting
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Toggling...</>
              : (isTwoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA')}
          </button>
        </div>
      </div>
    );
  };

  // ==================== NOTIFICATION PREFERENCES ====================

  const handleNotificationPreferences = () => {
    setIsDropdownOpen(false);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Bell size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Notification Preferences</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              { id: 'email', label: 'Email Notifications', icon: Mail, desc: 'Receive notifications via email' },
              { id: 'sms', label: 'SMS Notifications', icon: Smartphone, desc: 'Receive notifications via SMS' },
              { id: 'push', label: 'Push Notifications', icon: Bell, desc: 'Receive browser push notifications' },
              { id: 'birthday', label: 'Birthday Reminders', icon: Cake, desc: 'Get reminded about member birthdays' },
              { id: 'low_credit', label: 'Low Credit Alerts', icon: AlertTriangle, desc: 'Alert when SMS credits are low' },
              { id: 'weekly_digest', label: 'Weekly Digest', icon: BarChart3, desc: 'Receive weekly summary reports' }
            ].map((item) => (
              <div key={item.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '10px 12px',
                background: 'var(--cream)',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>
                    <item.icon size={14} style={{ marginRight: '8px', verticalAlign: '-2px' }} />
                    {item.label}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{item.desc}</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            showToastMessage('✅ Notification preferences saved!', 'success');
            closeModal();
          }}>Save Preferences</button>
        </div>
      </div>
    );
  };

  // ==================== ACTIVITY LOG ====================

  const handleActivityLog = () => {
    setIsDropdownOpen(false);
    const activities = [
      { id: 1, action: 'Logged in', timestamp: new Date().toLocaleString(), details: 'IP: 192.168.1.1' },
      { id: 2, action: 'Sent broadcast', timestamp: '2026-04-23 09:15:00', details: 'Sunday reminder to 1,247 members' },
      { id: 3, action: 'Updated member', timestamp: '2026-04-22 16:45:00', details: 'Edited Emmanuel Asante' },
      { id: 4, action: 'Created poll', timestamp: '2026-04-22 14:30:00', details: 'Service feedback poll' },
      { id: 5, action: 'Exported members', timestamp: '2026-04-21 11:20:00', details: 'Exported 124 members' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Activity Log</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {activities.map((activity) => (
              <div key={activity.id} style={{ 
                padding: '10px 12px', 
                borderBottom: '1px solid var(--border-light)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>
                    {activity.action}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                    {activity.timestamp}
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-mid)' }}>
                  {activity.details}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            showToastMessage('📥 Activity log exported', 'success');
            closeModal();
          }}><Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export</button>
        </div>
      </div>
    );
  };

  // ==================== THEME ACTIONS ====================

  const handleThemeChange = (theme) => {
    setCurrentTheme(theme);
    setIsThemeSubmenuOpen(false);
    setIsDropdownOpen(false);
    if (onThemeChange) {
      onThemeChange(theme);
    }
    showToastMessage(`🎨 Theme changed to ${theme}`, 'success');
  };

  const toggleThemeSubmenu = (e) => {
    e.stopPropagation();
    setIsThemeSubmenuOpen(!isThemeSubmenuOpen);
    setIsLanguageSubmenuOpen(false);
  };

  // ==================== LANGUAGE ACTIONS ====================

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    setIsLanguageSubmenuOpen(false);
    setIsDropdownOpen(false);
    if (onLanguageChange) {
      onLanguageChange(language);
    }
    showToastMessage(`🌐 Language changed to ${language}`, 'success');
  };

  const toggleLanguageSubmenu = (e) => {
    e.stopPropagation();
    setIsLanguageSubmenuOpen(!isLanguageSubmenuOpen);
    setIsThemeSubmenuOpen(false);
  };

  // ==================== SUPPORT ACTIONS ====================

  const handleHelpSupport = () => {
    setIsDropdownOpen(false);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Library size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Help & Support</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            {[
              { icon: BookOpen, title: 'Documentation', desc: 'Read our comprehensive documentation' },
              { icon: MessageSquare, title: 'Live Chat', desc: 'Chat with our support team' },
              { icon: Mail, title: 'Email Support', desc: 'support@faithconnect.com' },
              { icon: Smartphone, title: 'WhatsApp', desc: '+233 24 123 4567' },
              { icon: Video, title: 'Video Tutorials', desc: 'Watch our video guides' },
              { icon: HelpCircle, title: 'FAQ', desc: 'Frequently asked questions' }
            ].map((item) => (
              <div key={item.title} style={{ 
                padding: '12px', 
                background: 'var(--cream)', 
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gold-pale)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cream)'}
              onClick={() => {
                showToastMessage(`📚 Opening ${item.title}...`, 'info');
                closeModal();
              }}
              >
                <div style={{ marginBottom: '4px' }}><item.icon size={20} /></div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                  {item.title}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  const handleDocumentation = () => {
    setIsDropdownOpen(false);
    window.open('/docs', '_blank');
    showToastMessage('📖 Opening documentation...', 'info');
  };

  // ==================== FEEDBACK ====================

  const handleFeedback = () => {
    setIsDropdownOpen(false);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><MessageSquare size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Feedback</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">How would you rate your experience?</label>
            <div style={{ display: 'flex', gap: '12px', fontSize: '32px' }}>
              {['😡', '😕', '😐', '😊', '🤩'].map((emoji, i) => (
                <button key={i} className="btn btn-ghost btn-sm" style={{ fontSize: '32px' }} onClick={() => {
                  showToastMessage(`⭐ Rated ${i + 1} stars!`, 'success');
                }}>
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Your Feedback</label>
            <textarea className="form-textarea" rows="4" placeholder="Tell us what you think..." />
          </div>
          <div className="form-group">
            <label className="form-label">Would you like us to follow up?</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-ghost btn-sm">Yes</button>
              <button className="btn btn-ghost btn-sm">No</button>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            showToastMessage('✅ Feedback submitted! Thank you!', 'success');
            closeModal();
          }}>Submit Feedback</button>
        </div>
      </div>
    );
  };

  // ==================== SWITCH ADMIN ====================

  const handleSwitchAdmin = () => {
    setIsDropdownOpen(false);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><RefreshCw size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Switch Admin</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Select an admin to switch to:
          </p>
          <div style={{ display: 'grid', gap: '10px' }}>
            {[
              { name: 'Sis. Patricia Adu', role: 'Admin', email: 'patricia@gracechapel.org' },
              { name: 'Bro. Felix Owusu', role: 'Staff', email: 'felix@gracechapel.org' }
            ].map((admin) => (
              <div key={admin.email} style={{ 
                padding: '12px', 
                background: 'var(--cream)', 
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gold-pale)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cream)'}
              onClick={() => {
                showToastMessage(`🔄 Switched to ${admin.name}`, 'success');
                closeModal();
              }}
              >
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  background: '#4f46e5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '14px'
                }}>
                  {admin.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                    {admin.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                    {admin.role} · {admin.email}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // ==================== LOGOUT ALL DEVICES ====================

  const handleLogoutAllDevices = () => {
    setIsDropdownOpen(false);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Smartphone size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Logout All Devices</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            textAlign: 'center',
            padding: '20px 0'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><Smartphone size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--navy)' }}>
              Are you sure you want to logout from all devices?
            </div>
            <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginTop: '8px' }}>
              This will end all active sessions across all your devices.
            </p>
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--text-mid)'
            }}>
              You will be redirected to the login page.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(logoutAllDevices()).unwrap();
                console.log('✅ Logout all devices result:', result);
                
                // Clear local storage and auth state
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('remembered_email');
                dispatch(clearAuthState());
                
                closeModal();
                navigate('/login');
                showToastMessage('👋 Logged out from all devices successfully', 'info');
              } catch (error) {
                console.error('❌ Logout all devices error:', error);
                // Still clear local state even if API fails
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('remembered_email');
                dispatch(clearAuthState());
                closeModal();
                navigate('/login');
                showToastMessage('👋 Session cleared', 'info');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Logging out...</>
              : 'Logout All Devices'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== LOGOUT ====================

  const handleLogout = () => {
    setIsDropdownOpen(false);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><LogOut size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Logout</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            textAlign: 'center',
            padding: '20px 0'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><Hand size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--navy)' }}>
              Are you sure you want to logout?
            </div>
            <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginTop: '8px' }}>
              You will be redirected to the login page.
            </p>
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--text-mid)'
            }}>
              Your session will be securely ended.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm"
            disabled={isSubmitting}
            onClick={() => {
              dispatch(logoutUser());
              closeModal();
              navigate('/login');
              showToastMessage('👋 Logged out successfully', 'info');
            }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  };

  // ==================== PROFILE MENU ITEMS ====================

  const profileMenuItems = [
    {
      icon: User,
      label: 'My Profile',
      action: handleViewProfile,
      type: 'action'
    },
    {
      icon: Settings,
      label: 'Settings',
      action: handleSettings,
      type: 'action'
    },
    {
      icon: Rocket,
      label: 'Switch to Admin',
      action: handleSwitchAdmin,
      type: 'action'
    },
    { divider: true },
    {
      icon: Palette,
      label: `Theme: ${currentTheme}`,
      action: toggleThemeSubmenu,
      type: 'submenu',
      submenu: true,
      submenuItems: [
        { label: 'Light', icon: Sun, action: () => handleThemeChange('light') },
        { label: 'Dark', icon: Moon, action: () => handleThemeChange('dark') },
        { label: 'System', icon: Monitor, action: () => handleThemeChange('system') }
      ]
    },
    {
      icon: Globe,
      label: `Language: ${currentLanguage}`,
      action: toggleLanguageSubmenu,
      type: 'submenu',
      submenu: true,
      submenuItems: [
        { label: 'English', icon: '🇬🇧', action: () => handleLanguageChange('English') },
        { label: 'French', icon: '🇫🇷', action: () => handleLanguageChange('French') },
        { label: 'Spanish', icon: '🇪🇸', action: () => handleLanguageChange('Spanish') },
        { label: 'Portuguese', icon: '🇵🇹', action: () => handleLanguageChange('Portuguese') }
      ]
    },
    { divider: true },
    {
      icon: Library,
      label: 'Help & Support',
      action: handleHelpSupport,
      type: 'action'
    },
    {
      icon: BookOpen,
      label: 'Documentation',
      action: handleDocumentation,
      type: 'action'
    },
    {
      icon: MessageSquare,
      label: 'Feedback',
      action: handleFeedback,
      type: 'action'
    },
    { divider: true },
    {
      icon: Smartphone,
      label: 'Logout All Devices',
      action: handleLogoutAllDevices,
      type: 'action'
    },
    {
      icon: LogOut,
      label: 'Logout',
      action: handleLogout,
      type: 'logout'
    }
  ];

  // Get user initials for avatar with safe fallbacks
  const userInitials = getUserInitials(currentUser);
  const statusColor = getStatusColor(currentUser?.status);
  const displayName = String(userName || currentUser?.name || 'User');
  const displayEmail = String(userEmail || currentUser?.email || 'user@example.com');
  const displayRole = String(userRole || currentUser?.role || 'Member');
  const displayOrganization = String(organizationName || '');

  return (
    <header className="topbar" style={{ position: 'relative', zIndex: 1000, overflow: 'visible' }}>
      <div className="topbar-title">{title}</div>
      <div className="topbar-actions">
        {/* <div className="search-box">
          <span className="search-icon"><Search size={16} /></span>
          <input type="text" placeholder="Search members, messages…" />
        </div> */}
        <button className="btn btn-gold btn-sm" onClick={() => navigate('/broadcast')}>
          + Send SMS
        </button>
        <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => showToastMessage('🔔 3 new notifications', 'info')}>
          <div style={{ display: 'flex' }}><Bell size={20} /></div>
          <div className="notif-badge">3</div>
        </div>
        
        {/* Profile Avatar with Dropdown */}
        <div className="profile-dropdown-wrapper" style={{ position: 'relative', zIndex: 1001 }}>
          <div 
            ref={avatarRef}
            className={`avatar ${isDropdownOpen ? 'active' : ''}`} 
            onClick={toggleDropdown}
            title={displayName || 'User'}
            style={{
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1001,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#1a1a2e',
              color: '#f0e6d3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: '700',
              transition: 'all 0.3s ease',
              userSelect: 'none'
            }}
          >
            {userInitials}
            <div style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              fontSize: '8px',
              color: '#94a3b8',
              transition: 'transform 0.3s ease',
              background: '#ffffff',
              borderRadius: '50%',
              width: '14px',
              height: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </div>
          </div>

          {/* Dropdown Menu - Only render if user exists */}
          {isDropdownOpen && (
            <div 
              ref={dropdownRef}
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: '0',
                minWidth: '320px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                zIndex: 99999,
                overflow: 'visible',
                animation: 'slideDown 0.2s ease'
              }}
            >
              {/* User Info - Safely render user data */}
              <div style={{ 
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: '#f8fafc',
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#1a1a2e',
                  color: '#f0e6d3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '700',
                  position: 'relative',
                  flexShrink: 0
                }}>
                  {userInitials}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    border: '2px solid #ffffff',
                    background: statusColor
                  }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1a1a2e',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{displayName}</div>
                  <div style={{
                    fontSize: '12px',
                    color: '#94a3b8',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{displayEmail}</div>
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    marginTop: '4px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '500',
                      background: '#1a1a2e',
                      color: '#f0e6d3',
                      display: 'inline-block'
                    }}>{displayRole}</span>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '500',
                      background: currentUser?.status === 'Active' ? '#dcfce7' : '#fef3c7',
                      color: currentUser?.status === 'Active' ? '#22c55e' : '#f59e0b',
                      display: 'inline-block'
                    }}>{currentUser?.status || 'Active'}</span>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div style={{
                padding: '6px 0',
                maxHeight: '400px',
                overflowY: 'auto',
                overflowX: 'visible',
                background: '#ffffff'
              }}>
                {profileMenuItems.map((item, index) => {
                  if (item.divider) {
                    return <div key={`divider-${index}`} style={{
                      height: '1px',
                      background: '#e2e8f0',
                      margin: '4px 12px'
                    }} />;
                  }

                  const isLogout = item.type === 'logout';
                  const isSubmenu = item.type === 'submenu';
                  const isSubmenuOpen = isSubmenu && 
                    (item.label.includes('Theme') ? isThemeSubmenuOpen : isLanguageSubmenuOpen);

                  return (
                    <div key={item.label}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 20px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: '13px',
                          color: isLogout ? '#ef4444' : '#1a1a2e',
                          background: 'transparent',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isLogout ? '#fef2f2' : '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                        onClick={(e) => {
                          if (isSubmenu) {
                            item.action(e);
                          } else {
                            item.action();
                          }
                        }}
                      >
                        <span style={{
                          fontSize: '16px',
                          width: '20px',
                          textAlign: 'center',
                          flexShrink: 0,
                          display: 'inline-flex',
                          justifyContent: 'center'
                        }}><item.icon size={16} /></span>
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {isSubmenu && (
                          <span style={{
                            color: '#94a3b8',
                            fontSize: '16px',
                            marginLeft: 'auto',
                            transform: isSubmenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}>›</span>
                        )}
                      </div>

                      {/* Submenu Items */}
                      {isSubmenu && isSubmenuOpen && item.submenuItems && (
                        <div style={{
                          padding: '4px 0',
                          margin: '0 12px 4px 12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          {item.submenuItems.map((subItem) => (
                            <div
                              key={subItem.label}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '6px 16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '12px',
                                color: '#1a1a2e',
                                borderRadius: '4px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#e2e8f0';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                subItem.action();
                              }}
                            >
                              <span style={{ fontSize: '14px' }}>
                                {typeof subItem.icon === 'string' ? subItem.icon : <subItem.icon size={14} />}
                              </span>
                              <span>{subItem.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer - SAFELY render organization name */}
              <div style={{
                padding: '10px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
                color: '#94a3b8',
                background: '#f8fafc',
                borderRadius: '0 0 12px 12px'
              }}>
                <div style={{ fontFamily: 'JetBrains Mono, monospace' }}>Version 2.4.0</div>
                {displayOrganization && (
                  <div style={{ fontWeight: '500', color: '#1a1a2e', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayOrganization}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .profile-dropdown-wrapper {
          position: relative !important;
          z-index: 9999 !important;
        }

        .topbar {
          overflow: visible !important;
          position: relative !important;
          z-index: 1000 !important;
        }

        .topbar-actions {
          overflow: visible !important;
        }

        .avatar:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .avatar.active {
          box-shadow: 0 0 0 3px #d4af37;
          transform: scale(1.05);
        }

        .notif-badge {
          position: absolute;
          top: -4px;
          right: -6px;
          background: #ef4444;
          color: white;
          font-size: 10px;
          font-weight: 700;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #ffffff;
        }

        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }

        @media (max-width: 768px) {
          .profile-dropdown {
            position: fixed !important;
            top: 60px !important;
            right: 10px !important;
            left: 10px !important;
            min-width: auto !important;
            max-width: 100% !important;
            width: auto !important;
          }
        }

        @media (max-width: 480px) {
          .profile-dropdown {
            top: 56px !important;
            right: 5px !important;
            left: 5px !important;
            border-radius: 8px !important;
          }
        }
      `}</style>
    </header>
  );
};

export default Topbar;